import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { getToken } from 'src/auth';
import { api } from 'src/api/config';
import { setBreadcrumbs } from 'src/ui';
import { MerchantFormModal } from '../components/MerchantFormModal';
import { TerminalFormModal } from '../components/TerminalFormModal';

function MerchantConfigPageComponent({ token, setBreadcrumbs }) {
  // State for accounts list
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('enabled'); // 'all', 'enabled', 'pending'

  // State for expanded rows (to show terminals)
  const [expandedRows, setExpandedRows] = useState({});
  const [terminalData, setTerminalData] = useState({});
  const [terminalLoading, setTerminalLoading] = useState({});

  // State for modals
  const [merchantModalOpen, setMerchantModalOpen] = useState(false);
  const [merchantModalMode, setMerchantModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [merchantData, setMerchantData] = useState(null);

  const [terminalModalOpen, setTerminalModalOpen] = useState(false);
  const [terminalModalMode, setTerminalModalMode] = useState('create');
  const [selectedTerminal, setSelectedTerminal] = useState(null);

  // Fetch eligible accounts
  const fetchAccounts = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${api.hostname}/api/v1/pos/eligible-accounts/`, {
        headers: {
          'Authorization': `Bearer ${token.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch accounts: ${response.status}`);
      }

      const data = await response.json();
      setAccounts(data);
    } catch (err) {
      console.error('Failed to fetch eligible accounts:', err);
      setError(err.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    setBreadcrumbs([
      { label: 'SolventPay', href: '/solventpay' },
      { label: 'Merchant Config', href: '/solventpay/merchant-config' },
    ]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Fetch merchant data for an account
  const fetchMerchantData = async (accountId) => {
    try {
      const response = await fetch(`${api.hostname}/api/v1/pos/merchants/${accountId}/`, {
        headers: {
          'Authorization': `Bearer ${token.accessToken}`,
        },
      });

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch merchant: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      console.error('Failed to fetch merchant data:', err);
      return null;
    }
  };

  // Fetch terminals for an account
  const fetchTerminals = async (accountId) => {
    setTerminalLoading((prev) => ({ ...prev, [accountId]: true }));

    try {
      const response = await fetch(`${api.hostname}/api/v1/pos/merchants/${accountId}/terminals/`, {
        headers: {
          'Authorization': `Bearer ${token.accessToken}`,
        },
      });

      if (response.status === 404) {
        // Merchant doesn't exist in POS - backend has reset the flag
        // Refresh accounts list to update the UI
        console.warn(`Merchant not found for account ${accountId} - refreshing accounts list`);
        setTerminalData((prev) => ({ ...prev, [accountId]: [] }));
        setExpandedRows((prev) => ({ ...prev, [accountId]: false }));
        fetchAccounts();
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch terminals: ${response.status}`);
      }

      const data = await response.json();
      setTerminalData((prev) => ({ ...prev, [accountId]: data }));
    } catch (err) {
      console.error('Failed to fetch terminals:', err);
      setTerminalData((prev) => ({ ...prev, [accountId]: [] }));
    } finally {
      setTerminalLoading((prev) => ({ ...prev, [accountId]: false }));
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (accountId) => {
    const isExpanding = !expandedRows[accountId];
    setExpandedRows((prev) => ({ ...prev, [accountId]: isExpanding }));

    if (isExpanding && !terminalData[accountId]) {
      fetchTerminals(accountId);
    }
  };

  // Handle Enable Payments click
  const handleEnablePayments = (account) => {
    setSelectedAccount(account);
    setMerchantData(null);
    setMerchantModalMode('create');
    setMerchantModalOpen(true);
  };

  // Handle Configure click
  const handleConfigure = async (account) => {
    setSelectedAccount(account);
    const merchant = await fetchMerchantData(account.id);
    setMerchantData(merchant);

    // If merchant doesn't exist in POS backend, use create mode
    // Also refresh accounts to update the flag (backend resets it on 404)
    if (!merchant) {
      fetchAccounts();
    }

    setMerchantModalMode(merchant ? 'edit' : 'create');
    setMerchantModalOpen(true);
  };

  // Handle Add Terminal click
  const handleAddTerminal = (account) => {
    setSelectedAccount(account);
    setSelectedTerminal(null);
    setTerminalModalMode('create');
    setTerminalModalOpen(true);
  };

  // Handle Edit Terminal click
  const handleEditTerminal = (account, terminal) => {
    setSelectedAccount(account);
    setSelectedTerminal(terminal);
    setTerminalModalMode('edit');
    setTerminalModalOpen(true);
  };

  // Handle Delete Terminal
  const handleDeleteTerminal = async (account, terminal) => {
    if (!window.confirm(`Are you sure you want to delete terminal "${terminal.name}"?`)) {
      return;
    }

    try {
      const response = await fetch(
        `${api.hostname}/api/v1/pos/merchants/${account.id}/terminals/${terminal.id}/`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token.accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete terminal: ${response.status}`);
      }

      // Refresh terminals
      fetchTerminals(account.id);
    } catch (err) {
      console.error('Failed to delete terminal:', err);
      alert(`Failed to delete terminal: ${err.message}`);
    }
  };

  // Handle merchant modal save
  const handleMerchantSave = async (formData) => {
    try {
      const url = merchantModalMode === 'create'
        ? `${api.hostname}/api/v1/pos/merchants/create/`
        : `${api.hostname}/api/v1/pos/merchants/${selectedAccount.id}/`;

      const method = merchantModalMode === 'create' ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          terra_account_id: selectedAccount.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Request failed: ${response.status}`);
      }

      const responseData = await response.json();

      // Don't close modal on create - let the API key dialog show first
      // The modal will close itself after user acknowledges the API key
      if (merchantModalMode !== 'create') {
        setMerchantModalOpen(false);
      }
      fetchAccounts(); // Refresh list

      // Return response data so the modal can get the api_key
      return responseData;
    } catch (err) {
      console.error('Failed to save merchant:', err);
      throw err;
    }
  };

  // Handle terminal modal save
  const handleTerminalSave = async (formData) => {
    try {
      const url = terminalModalMode === 'create'
        ? `${api.hostname}/api/v1/pos/merchants/${selectedAccount.id}/terminals/`
        : `${api.hostname}/api/v1/pos/merchants/${selectedAccount.id}/terminals/${selectedTerminal.id}/`;

      const method = terminalModalMode === 'create' ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Request failed: ${response.status}`);
      }

      setTerminalModalOpen(false);
      fetchTerminals(selectedAccount.id);
    } catch (err) {
      console.error('Failed to save terminal:', err);
      throw err;
    }
  };

  // Filter accounts by search term and status
  const filteredAccounts = accounts.filter((account) => {
    // Status filter
    if (statusFilter === 'enabled' && !account.pos_payments_enabled) return false;
    if (statusFilter === 'pending' && account.pos_payments_enabled) return false;

    // Search filter
    const searchLower = searchTerm.toLowerCase();
    return (
      account.account_name?.toLowerCase().includes(searchLower) ||
      account.fi_name?.toLowerCase().includes(searchLower) ||
      account.account_email?.toLowerCase().includes(searchLower) ||
      String(account.id).includes(searchLower)
    );
  });

  // Count for status badges
  const enabledCount = accounts.filter((a) => a.pos_payments_enabled).length;
  const pendingCount = accounts.filter((a) => !a.pos_payments_enabled).length;

  if (loading && accounts.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Merchant Configuration</Typography>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">
                All ({accounts.length})
              </MenuItem>
              <MenuItem value="enabled">
                <Box display="flex" alignItems="center" gap={1}>
                  Enabled
                  <Chip label={enabledCount} size="small" color="success" sx={{ height: 20 }} />
                </Box>
              </MenuItem>
              <MenuItem value="pending">
                <Box display="flex" alignItems="center" gap={1}>
                  Pending
                  <Chip label={pendingCount} size="small" color="warning" sx={{ height: 20 }} />
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder="Search accounts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchAccounts}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50}></TableCell>
              <TableCell>Account Name</TableCell>
              <TableCell>ISO</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAccounts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {searchTerm ? 'No accounts match your search' : 'No eligible accounts found'}
                </TableCell>
              </TableRow>
            ) : (
              filteredAccounts.map((account) => (
                <React.Fragment key={account.id}>
                  <TableRow hover>
                    <TableCell>
                      {account.pos_payments_enabled && (
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpansion(account.id)}
                        >
                          {expandedRows[account.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {account.account_name}
                      </Typography>
                      {account.account_dba && (
                        <Typography variant="caption" color="text.secondary">
                          DBA: {account.account_dba}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{account.fi_name}</TableCell>
                    <TableCell>{account.account_email}</TableCell>
                    <TableCell>
                      {account.pos_payments_enabled ? (
                        <Chip label="Enabled" color="success" size="small" />
                      ) : (
                        <Chip label="Pending" color="warning" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {account.pos_payments_enabled ? (
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleConfigure(account)}
                        >
                          Configure
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => handleEnablePayments(account)}
                        >
                          Enable Payments
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Expanded row for terminals */}
                  {account.pos_payments_enabled && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 0, px: 0 }}>
                        <Collapse in={expandedRows[account.id]} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 4, bgcolor: 'grey.50' }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                              <Typography variant="subtitle2">Terminals</Typography>
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => handleAddTerminal(account)}
                              >
                                Add Terminal
                              </Button>
                            </Box>

                            {terminalLoading[account.id] ? (
                              <Box display="flex" justifyContent="center" py={2}>
                                <CircularProgress size={24} />
                              </Box>
                            ) : (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Terminal ID</TableCell>
                                    <TableCell>Location</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(terminalData[account.id] || []).length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={5} align="center">
                                        No terminals configured
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    (terminalData[account.id] || []).map((terminal) => (
                                      <TableRow key={terminal.id}>
                                        <TableCell>{terminal.name}</TableCell>
                                        <TableCell>
                                          <Typography variant="body2" fontFamily="monospace">
                                            {terminal.terminal_id}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>{terminal.location || '-'}</TableCell>
                                        <TableCell>
                                          <Box display="flex" gap={0.5}>
                                            <Chip
                                              label={terminal.is_active ? 'Active' : 'Inactive'}
                                              color={terminal.is_active ? 'success' : 'default'}
                                              size="small"
                                            />
                                          </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Tooltip title="Edit">
                                            <IconButton
                                              size="small"
                                              onClick={() => handleEditTerminal(account, terminal)}
                                            >
                                              <EditIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Delete">
                                            <IconButton
                                              size="small"
                                              color="error"
                                              onClick={() => handleDeleteTerminal(account, terminal)}
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </TableCell>
                                      </TableRow>
                                    ))
                                  )}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Merchant Modal */}
      <MerchantFormModal
        open={merchantModalOpen}
        onClose={() => setMerchantModalOpen(false)}
        onSave={handleMerchantSave}
        mode={merchantModalMode}
        account={selectedAccount}
        merchantData={merchantData}
      />

      {/* Terminal Modal */}
      <TerminalFormModal
        open={terminalModalOpen}
        onClose={() => setTerminalModalOpen(false)}
        onSave={handleTerminalSave}
        mode={terminalModalMode}
        terminal={selectedTerminal}
      />
    </Box>
  );
}

const mapStateToProps = (state) => ({
  token: getToken(state),
});

const mapDispatchToProps = {
  setBreadcrumbs,
};

export const MerchantConfigPage = connect(mapStateToProps, mapDispatchToProps)(MerchantConfigPageComponent);
